import Cookies, { CookieSetOptions } from 'universal-cookie';
import { CookieStorage, LocalStorage, SessionStorage } from '../types/Storage';

const cookieStorage = new Cookies();

type GuardedStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

// Safari can leave localStorage/sessionStorage null mid-session, which throws on access.
// Every call is guarded and falls back to an in-memory store for the lifetime of the page.
// Sentry (Safari) -> "null is not an object (evaluating 'localStorage.removeItem')"
const createGuardedStorage = (getStorageArea: () => Storage): GuardedStorage => {
    const memory = new Map<string, string>();

    return {
        getItem: (key) => {
            if (memory.has(key)) {
                return memory.get(key) ?? null;
            }

            try {
                return getStorageArea().getItem(key);
            } catch {
                return null;
            }
        },
        setItem: (key, value) => {
            try {
                getStorageArea().setItem(key, String(value));
                memory.delete(key);
            } catch {
                memory.set(key, String(value));
            }
        },
        removeItem: (key) => {
            try {
                getStorageArea().removeItem(key);
                memory.delete(key);
            } catch {
                memory.delete(key);
            }
        },
    };
};

export const guardedLocalStorage = createGuardedStorage(() => window.localStorage);

export const guardedSessionStorage = createGuardedStorage(() => window.sessionStorage);

export const removeLocalStorage = <T extends keyof LocalStorage>(key: T) => {
    guardedLocalStorage.removeItem(key);
};

export const setLocalStorage = <T extends keyof LocalStorage>(key: T, value: LocalStorage[T]) => {
    guardedLocalStorage.setItem(key, JSON.stringify(value));
};

export const setCookieStorage = <T extends keyof CookieStorage>(key: T, value: CookieStorage[T], options?: CookieSetOptions) => {
    cookieStorage.set(key, JSON.stringify(value), options);
};

export const setLocalStorageString = <T extends keyof LocalStorage>(key: T, value: string) => {
    guardedLocalStorage.setItem(key, value);
};

export const setCookieStorageString = <T extends keyof CookieStorage>(key: T, value: string, options?: CookieSetOptions) => {
    cookieStorage.set(key, value, options);
};

const getStorage = (key: string, storage: GuardedStorage) => {
    const item = storage.getItem(key);
    if (item) {
        try {
            return JSON.parse(item);
        } catch {
            return null;
        }
    }
    return null;
};

const getCookie = (key: string, storage: typeof cookieStorage) => {
    const item = storage.get(key);
    if (item) {
        try {
            return JSON.parse(item);
        } catch {
            return null;
        }
    }
    return null;
};

export const getLocalStorage = <T extends keyof LocalStorage>(key: T): LocalStorage[T] => getStorage(key, guardedLocalStorage);

export const getCookieStorage = <T extends keyof CookieStorage>(key: T): CookieStorage[T] => getCookie(key, cookieStorage);

export const getLocalStorageString = <T extends keyof LocalStorage>(key: T): string => guardedLocalStorage.getItem(key);

export const getCookieStorageString = <T extends keyof CookieStorage>(key: T): string => cookieStorage.get(key);

export const setSessionStorageString = <T extends keyof SessionStorage>(key: T, value: string) => {
    guardedSessionStorage.setItem(key, value);
};

export const getSessionStorageString = <T extends keyof SessionStorage>(key: T): string => guardedSessionStorage.getItem(key) || '';

export const setSessionStorage = <T extends keyof SessionStorage>(key: T, value: SessionStorage[T]) => {
    guardedSessionStorage.setItem(key, JSON.stringify(value));
};

export const getSessionStorage = <T extends keyof SessionStorage>(key: T): SessionStorage[T] => getStorage(key, guardedSessionStorage);

export const removeSessionStorage = <T extends keyof SessionStorage>(key: T) => {
    guardedSessionStorage.removeItem(key);
};
